import { LinkButton } from '@pmi/dsm-react';
import { ChevronLeftSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import AgreementCard from '../../../../../shared/components/InviteWizard/AgreementCard';
import AgreementProductSelector from '../../../../../shared/components/InviteWizard/AgreementProductSelector';
import AgreementSearchFilters from '../../../../../shared/components/InviteWizard/AgreementSearchFilters';
import wizardStyles from '../../../../../shared/components/InviteWizard/wizard.module.scss';
import { AgreementApiModel } from '../../../../../shared/models/DealModel';
import { ProductApiModel } from '../../../../../shared/models/ProductModel';
import { User } from '../../../../../shared/models/UserModels';
import { dealHasExpired } from '../../../../../shared/utils/dataHelpers';
import editUserModalStyles from '../BulkActionMenu/EditUserAction/EditUsersModal.module.scss';
import { useGetUserCurrentProductsQuery } from '../ViewUserModal/UserCurrentProductsTable/apiSlice';
import styles from './editModalContent.module.scss';

export interface ProductListData {
  product: ProductApiModel;
  agreements: AgreementApiModel[];
}

export interface EditModalContentProps {
  agreements: AgreementApiModel[];
  user: User;
  isRestoreMode?: boolean;
  onCancel: () => void;
  mode: 'full' | 'product' | 'restore';
}

export default function EditModalContent({
  user,
  isRestoreMode = false,
  agreements,
  mode,
  onCancel,
}: EditModalContentProps) {
  const [displayAgreements, setDisplayAgreements] =
    useState<AgreementApiModel[]>(agreements);
  const [selectedAgreement, setSelectedAgreement] =
    useState<AgreementApiModel | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [step, setStep] = useState<'agreements' | 'products'>('agreements');
  const [loading, setLoading] = useState(true);

  const {
    data: userAssignedProducts,
    isLoading: isUserAssignedProductsLoading,
  } = useGetUserCurrentProductsQuery(
    {
      userId: user?.id.toString(),
    },
    {
      skip: user === undefined || !user,
    },
  );

  useEffect(() => {
    if (!isUserAssignedProductsLoading) {
      setLoading(false);
    }
  }, [isUserAssignedProductsLoading]);

  const userInfo = user?.personName || user?.email || 'this user';

  return (
    <div className="modal-container position-relative">
      {['full', 'restore', 'add-user-product'].includes(mode) && (
        <div className={`${styles.stepperContainer} mb-3`}>
          <ul className="stepper mb-4">
            <li
              className={`${styles.stepperItem} ${
                step === 'agreements' ? styles.stepperItem_active : ''
              } ${step === 'products' ? styles.stepperItem_finished : ''}`}
            >
              <span className="stepperPoint">
                <span className="stepper-title">Select Agreement</span>
                <span className="stepper-number">1</span>
              </span>
            </li>
            <li
              className={`${styles.stepperItem} ${
                step === 'products' ? styles.stepperItem_active : ''
              }`}
            >
              <div className="stepperPoint">
                <span className="stepper-title">Select Products</span>
                <span className="stepper-number">2</span>
              </div>
            </li>
          </ul>
        </div>
      )}
      {step === 'agreements' && (
        <LinkButton
          size="sm"
          className="wizard-back-btn mb-4"
          icon={ChevronLeftSolidIcon}
          iconAlign="left"
          titleText="Back to Profile"
          onClick={() => onCancel()}
        />
      )}
      {step === 'products' && (
        <LinkButton
          size="sm"
          className="wizard-back-btn mb-4"
          icon={ChevronLeftSolidIcon}
          iconAlign="left"
          titleText="Back to Agreements"
          onClick={() => setStep('agreements')}
        />
      )}
      {isRestoreMode ? (
        <div className={editUserModalStyles.enableAccessContainer}>
          <div className={editUserModalStyles.title}>
            Enable access for {userInfo}{' '}
          </div>
          <div className={`${editUserModalStyles.description} my-3`}>
            To enable access, please select the products they should be
            assigned. You must select at least one Product Group to enable.
          </div>
        </div>
      ) : (
        <div className="d-block">
          {step === 'agreements' && (
            <>
              <h3>Select agreement to select products for {userInfo}</h3>
              <hr className="my-3" />
              <AgreementSearchFilters
                agreements={agreements}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                loading={loading}
                onFilteredDealsChange={setDisplayAgreements}
              />
              <div
                className={`d-block mt-4 mb-2 ${wizardStyles.agreementLabel}`}
              >
                {loading && (
                  <Spinner size="sm" className="text-primary d-block m-auto" />
                )}
                {!loading &&
                  displayAgreements.length > 0 &&
                  displayAgreements.map((a) => (
                    <AgreementCard
                      deal={a}
                      title={a.dealName}
                      btnLabel="Select Agreement"
                      expired={dealHasExpired(a)}
                      onClick={() => {
                        setSelectedAgreement(a);
                        setStep('products');
                      }}
                      key={`editUserAgreementSelection${a.id.toString()}`}
                    />
                  ))}
                {!loading && displayAgreements.length === 0 && (
                  <p className="text-muted fw-normal small">
                    No{' '}
                    {activeTab > 0
                      ? 'expired agreement found.'
                      : 'active agreement found.'}
                  </p>
                )}
              </div>
            </>
          )}
          {step === 'products' && selectedAgreement && (
            <AgreementProductSelector
              defaultProducts={[]}
              agreement={selectedAgreement}
              headerText={`Select one or more products for ${userInfo}`}
              people={[user]}
              action="add"
              hasBackButton
              disabledProductIds={
                userAssignedProducts?.map((p) => p.accountProductId) ?? []
              }
              onSuccess={() => onCancel()}
            />
          )}
        </div>
      )}
    </div>
  );
}
